/**
 * @file   src\components\Common\NiceToHave\NiceToHave.js
 * @brief  This file is responsible for nice to have selection in want ad creation
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import { components } from "react-select";
import NiceToHaveSelect from "./NiceToHaveSelect";
import { getNiceToHaves } from "../../../services/commonServices";
import {
  BUY_LAND,
  BUY,
  IS_LAND,
  INVENTORY_LAND,
  NICE_2_HAVES_CATEGORIES_MASTER_LIST,
  NICE_2_HAVES_LOT_LAND_CATEGORIES,
} from "../../../constants/common";
import PlaceHolderIcon from "../../../images/dpdown-placeholder-icon.svg";

const MoreSelectedBadge = ({ items }) => {
  const length = items.length;
  const label = `+ ${length}`;
  return <div className="root">{label}</div>;
};

const MultiValue = ({ index, getValue, ...props }) => {
  const maxToShow = 3;
  const overflow = getValue()
    .slice(maxToShow)
    .map((x) => x.label);
  let icon = props.data.Icon ?? props.data.icon;

  return index < maxToShow ? (
    <components.MultiValue {...props}>
      <span className="nice-have-iconwrap">
        <img
          src={
            icon
              ? (() => {
                try {
                  return require("../../../images/" + icon);
                } catch (e) {
                  return PlaceHolderIcon;
                }
              })()
              : PlaceHolderIcon
          }
          style={{ width: 16, height: 16 }}
          alt={props.data.label}
        />
      </span>
      <span>{props.data.label}</span>
    </components.MultiValue>
  ) : index === maxToShow ? (
    <MoreSelectedBadge items={overflow} />
  ) : null;
};

const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#eee";
  if (isActive) bg = "#B2D4FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
    rest,
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <div className="form-check ms-2" tabIndex="-1">
        <input
          className="form-check-input"
          type="checkbox"
          value=""
          checked={isSelected}
          readOnly
        />
        <span className="form-check-label checkbox-label">
          {children}
        </span>
      </div>
    </components.Option>
  );
};

const SelectInputs = ({ isLotLand, selectedNiceToHave, handleFormValues, propertyType, niceToHavesList, selectedniceToHavesList, disabled }) => {

  const list = isLotLand ? NICE_2_HAVES_LOT_LAND_CATEGORIES : NICE_2_HAVES_CATEGORIES_MASTER_LIST;


  return list?.map((item) => (
    <div className="multi-select-wrap">
      <label className="form-label nice-to-have-label">{item.title}</label>
      <NiceToHaveSelect
        placeholder={item.placeholder}
        selectedNiceToHave={selectedNiceToHave}
        niceToHavesList={niceToHavesList?.[item.category]}
        tab={item.category}
        handleFormValues={handleFormValues}
        propertyType={propertyType}
        componentValue={{ Option: InputOption, MultiValue }}
        selectedniceToHavesList={selectedniceToHavesList}
        disabled={disabled}
      />
    </div>
  ))
}

const NiceToHave = (props) => {
  const {
    selectedNiceToHave,
    handleFormValues,
    propertyType,
    adType,
    selectedniceToHavesList,
    isFromInventory,
    proprtyPurpose,
  } = props;

  const disabledInputs = props?.disabled || !propertyType

  const [niceToHavesList, setNiceToHavesList] = useState([]);

  useEffect(() => {
    if (propertyType) {
      let param;
      if (isFromInventory) {
        param = propertyType == IS_LAND ? INVENTORY_LAND : 'inv';
        // param = propertyType == IS_LAND ? INVENTORY_LAND : checkInventoryType(proprtyPurpose);
      } else {
        param = (propertyType == 6 ? BUY_LAND : adType) || BUY;
      }
      getNiceToHaves(param)
        .then((response) => {
          setNiceToHavesList(response.result);
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [propertyType, adType, proprtyPurpose]);

  return (
    <div className="multi-select-outerwrap pb-24">
      <h5 className="mb-1 nice-tohave-head">{props.title}</h5>
      <h6 className="mb-12 nice-tohave-para">
        These are the ideal features that make a home unique. Select up to
        16 items in total.
      </h6>
      <SelectInputs
        isLotLand={Number(propertyType) === Number(IS_LAND)}
        selectedNiceToHave={selectedNiceToHave}
        handleFormValues={handleFormValues}
        propertyType={propertyType}
        niceToHavesList={niceToHavesList}
        selectedniceToHavesList={selectedniceToHavesList}
        disabled={disabledInputs}
      />
    </div>
  )

};
export default NiceToHave;
